import React, { useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaLock,
  FaTimes,
} from "react-icons/fa";

import "./Login.css";
import { API_URL } from "../../config";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const [message, setMessage] = useState(null);

  const showStatus = (type, text) => {
    setMessage({ type, text });
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password) {
      showStatus("error", "Bitte E-Mail und Passwort eingeben.");
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "Anmeldung fehlgeschlagen.");
      }

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("role", data.role);
      localStorage.setItem("email", data.email);
      localStorage.setItem("userId", String(data.userId));

      onLogin();
    } catch (error) {
      console.error("Loginfehler:", error);

      showStatus(
        "error",
        error.message || "Die Anmeldung ist fehlgeschlagen."
      );
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotEmail(email.trim());
    setMessage(null);
    setShowForgotPassword(true);
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();

    if (!forgotEmail.trim()) {
      showStatus("error", "Bitte eine E-Mail-Adresse eingeben.");
      return;
    }

    setForgotLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${API_URL}/api/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: forgotEmail.trim().toLowerCase(),
          }),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || "Die Anfrage konnte nicht verarbeitet werden."
        );
      }

      showStatus(
        "success",
        "Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen gesendet."
      );
    } catch (error) {
      console.error("Passwort-Reset-Fehler:", error);

      showStatus(
        "error",
        error.message || "Die E-Mail konnte nicht versendet werden."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background-shape login-shape-one" />
      <div className="login-background-shape login-shape-two" />

      <main className="login-card">
        <div className="login-brand">
          <img
            src={`${process.env.PUBLIC_URL}/Images/qroof-logo.png`}
            alt="QRoof"
            className="login-logo"
          />

          <div>
            <p className="login-eyebrow">QRoof</p>
            <h1>Willkommen zurück</h1>
          </div>
        </div>

        <p className="login-description">
          Melde dich an, um Arbeitsorte zu erfassen und Einträge zu
          verwalten.
        </p>

        {message && (
          <div
            className={`login-status login-status-${message.type}`}
            role="status"
          >
            {message.type === "success" ? (
              <FaCheckCircle />
            ) : (
              <FaExclamationTriangle />
            )}

            <span>{message.text}</span>

            <button
              type="button"
              aria-label="Meldung schließen"
              onClick={() => setMessage(null)}
            >
              <FaTimes />
            </button>
          </div>
        )}

        <form className="login-form" onSubmit={handleLogin}>
          <div className="login-field">
            <label htmlFor="login-email">E-Mail-Adresse</label>

            <div className="login-input-wrapper">
              <FaEnvelope className="login-input-icon" />

              <input
                id="login-email"
                type="email"
                placeholder="name@beispiel.de"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="login-field">
            <div className="password-label-row">
              <label htmlFor="login-password">Passwort</label>

              <button
                type="button"
                className="forgot-link"
                onClick={openForgotPassword}
              >
                Passwort vergessen?
              </button>
            </div>

            <div className="login-input-wrapper">
              <FaLock className="login-input-icon" />

              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                placeholder="Passwort eingeben"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                disabled={loading}
                required
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? "Passwort verbergen"
                    : "Passwort anzeigen"
                }
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-spinner" />
                Anmeldung läuft …
              </>
            ) : (
              <>
                <FaKey />
                Anmelden
              </>
            )}
          </button>
        </form>

        <footer className="login-footer">
          <span>QRoof Zeiterfassung</span>
          <span>•</span>
          <span>Sichere Anmeldung</span>
        </footer>
      </main>

      {showForgotPassword && (
        <div className="forgot-modal-overlay">
          <div
            className="forgot-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
          >
            <div className="forgot-modal-header">
              <div className="forgot-icon">
                <FaKey />
              </div>

              <button
                type="button"
                className="forgot-close-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setMessage(null);
                }}
                aria-label="Dialog schließen"
              >
                <FaTimes />
              </button>
            </div>

            <h2 id="forgot-password-title">
              Passwort zurücksetzen
            </h2>

            <p>
              Gib deine E-Mail-Adresse ein. Du erhältst einen
              zeitlich begrenzten Link zum Erstellen eines neuen
              Passworts.
            </p>

            <form onSubmit={handleForgotPassword}>
              <div className="login-field">
                <label htmlFor="forgot-email">E-Mail-Adresse</label>

                <div className="login-input-wrapper">
                  <FaEnvelope className="login-input-icon" />

                  <input
                    id="forgot-email"
                    type="email"
                    placeholder="name@beispiel.de"
                    value={forgotEmail}
                    onChange={(event) =>
                      setForgotEmail(event.target.value)
                    }
                    autoComplete="email"
                    disabled={forgotLoading}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="forgot-submit-button"
                disabled={forgotLoading}
              >
                {forgotLoading ? (
                  <>
                    <span className="login-spinner" />
                    Wird versendet …
                  </>
                ) : (
                  "Link anfordern"
                )}
              </button>

              <button
                type="button"
                className="forgot-cancel-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setMessage(null);
                }}
              >
                Abbrechen
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;